import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { auth } from '../../firebase/config';
import {
  getUserOrganization,
  getSearchTerms,
  createSearchTerm,
  deleteSearchTerm,
  toggleSearchTermStatus
} from '../../firebase/firestore';
import {
  Search,
  Plus,
  Trash2,
  Edit2,
  TrendingUp,
  TrendingDown,
  X,
  Check,
  AlertCircle
} from 'lucide-react';

function SearchTerms() {
  const [searchTerms, setSearchTerms] = useState([]);
  const [organization, setOrganization] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newTerm, setNewTerm] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const user = auth.currentUser;
      if (!user) return;

      // Get user's organization
      const org = await getUserOrganization(user.uid);
      if (!org) {
        console.log('No organization found');
        setLoading(false);
        return;
      }
      setOrganization(org);

      // Get search terms for organization
      const terms = await getSearchTerms(org.id);
      setSearchTerms(terms);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTerm = async () => {
    if (!newTerm.trim() || !organization) return;

    try {
      const user = auth.currentUser;
      if (!user) return;

      const newSearchTerm = await createSearchTerm(organization.id, {
        term: newTerm.trim(),
        userId: user.uid,
        active: true,
        platforms: organization.platforms || ['chatgpt', 'gemini', 'claude', 'perplexity'],
        priority: 'medium'
      });

      setSearchTerms([newSearchTerm, ...searchTerms]);
      setNewTerm('');
      setShowAddModal(false);
    } catch (error) {
      console.error('Error adding search term:', error);
    }
  };

  const handleDeleteTerm = async (id) => {
    try {
      await deleteSearchTerm(id);
      setSearchTerms(searchTerms.filter(term => term.id !== id));
    } catch (error) {
      console.error('Error deleting search term:', error);
    }
  };

  const handleToggleActive = async (id, currentActive) => {
    try {
      await toggleSearchTermStatus(id, !currentActive);
      setSearchTerms(searchTerms.map(term =>
        term.id === id ? { ...term, active: !currentActive } : term
      ));
    } catch (error) {
      console.error('Error toggling search term status:', error);
    }
  };

  const getPositionColor = (position) => {
    if (position === 0) return 'text-gray-400';
    if (position <= 3) return 'text-green-400';
    if (position <= 5) return 'text-blue-400';
    if (position <= 10) return 'text-yellow-400';
    return 'text-gray-400';
  };

  const getChangeIcon = (change) => {
    if (change > 0) return <TrendingUp className="w-4 h-4 text-green-400" />;
    if (change < 0) return <TrendingDown className="w-4 h-4 text-red-400" />;
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between"
      >
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Search Terms</h1>
          <p className="text-gray-400">
            Manage the search terms you're tracking across AI engines
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center space-x-2 bg-gradient-to-r from-primary-500 to-secondary-500 px-6 py-3 rounded-lg hover:shadow-lg transition-all"
        >
          <Plus className="w-5 h-5" />
          <span>Add Search Term</span>
        </button>
      </motion.div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-dark-800/50 backdrop-blur-sm border border-white/10 rounded-xl p-6"
        >
          <p className="text-sm text-gray-400 mb-2">Total Terms</p>
          <h3 className="text-3xl font-bold text-white">{searchTerms.length}</h3>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-dark-800/50 backdrop-blur-sm border border-white/10 rounded-xl p-6"
        >
          <p className="text-sm text-gray-400 mb-2">Active Terms</p>
          <h3 className="text-3xl font-bold text-white">
            {searchTerms.filter(t => t.active).length}
          </h3>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-dark-800/50 backdrop-blur-sm border border-white/10 rounded-xl p-6"
        >
          <p className="text-sm text-gray-400 mb-2">Avg. Position</p>
          <h3 className="text-3xl font-bold text-white">
            {searchTerms.length > 0
              ? (searchTerms.reduce((acc, t) => acc + (t.position || 0), 0) / searchTerms.length).toFixed(1)
              : '0'}
          </h3>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-dark-800/50 backdrop-blur-sm border border-white/10 rounded-xl p-6"
        >
          <p className="text-sm text-gray-400 mb-2">Total Citations</p>
          <h3 className="text-3xl font-bold text-white">
            {searchTerms.reduce((acc, t) => acc + (t.citations || 0), 0)}
          </h3>
        </motion.div>
      </div>

      {/* Search Terms Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="bg-dark-800/50 backdrop-blur-sm border border-white/10 rounded-xl overflow-hidden"
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-dark-700/50 border-b border-white/10">
              <tr>
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-300">
                  Search Term
                </th>
                <th className="text-center px-6 py-4 text-sm font-semibold text-gray-300">
                  Avg. Position
                </th>
                <th className="text-center px-6 py-4 text-sm font-semibold text-gray-300">
                  Change
                </th>
                <th className="text-center px-6 py-4 text-sm font-semibold text-gray-300">
                  Citations
                </th>
                <th className="text-center px-6 py-4 text-sm font-semibold text-gray-300">
                  Last Checked
                </th>
                <th className="text-center px-6 py-4 text-sm font-semibold text-gray-300">
                  Status
                </th>
                <th className="text-right px-6 py-4 text-sm font-semibold text-gray-300">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {searchTerms.map((term, index) => (
                <motion.tr
                  key={term.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.6 + index * 0.1 }}
                  className="hover:bg-dark-700/30 transition-colors"
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-3">
                      <Search className="w-5 h-5 text-primary-400 flex-shrink-0" />
                      <span className="text-white font-medium">{term.term}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className={`text-2xl font-bold ${getPositionColor(term.position || 0)}`}>
                      {term.position > 0 ? `#${term.position}` : '-'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-center space-x-2">
                      {getChangeIcon(term.change || 0)}
                      {term.change && term.change !== 0 && (
                        <span className={`text-sm font-medium ${term.change > 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {Math.abs(term.change)}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className="text-white font-medium">{term.citations || 0}</span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className="text-sm text-gray-400">{term.lastChecked || 'Never'}</span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <button
                      onClick={() => handleToggleActive(term.id, term.active)}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                        term.active
                          ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                          : 'bg-gray-500/20 text-gray-400 hover:bg-gray-500/30'
                      }`}
                    >
                      {term.active ? 'Active' : 'Paused'}
                    </button>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        className="p-2 text-gray-400 hover:text-primary-400 hover:bg-dark-700/50 rounded-lg transition-all"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteTerm(term.id)}
                        className="p-2 text-gray-400 hover:text-red-400 hover:bg-dark-700/50 rounded-lg transition-all"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>

        {searchTerms.length === 0 && (
          <div className="py-12 text-center">
            <Search className="w-16 h-16 mx-auto mb-4 text-gray-600" />
            <h3 className="text-lg font-semibold text-white mb-2">No search terms yet</h3>
            <p className="text-gray-400 mb-6">
              Add your first search term to start tracking your GEO performance
            </p>
            <button
              onClick={() => setShowAddModal(true)}
              className="bg-gradient-to-r from-primary-500 to-secondary-500 px-6 py-3 rounded-lg hover:shadow-lg transition-all"
            >
              Add Search Term
            </button>
          </div>
        )}
      </motion.div>

      {/* Add Term Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-dark-800 border border-white/10 rounded-xl p-6 max-w-md w-full"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">Add Search Term</h2>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Search Term
                </label>
                <input
                  type="text"
                  value={newTerm}
                  onChange={(e) => setNewTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddTerm()}
                  placeholder="e.g., best CRM software for small business"
                  className="w-full bg-dark-700 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-primary-500 transition-colors"
                  autoFocus
                />
                <p className="mt-2 text-xs text-gray-400">
                  <AlertCircle className="w-3 h-3 inline mr-1" />
                  Choose terms relevant to your organization and industry
                </p>
              </div>

              <div className="flex items-center justify-end space-x-3 pt-4">
                <button
                  onClick={() => setShowAddModal(false)}
                  className="px-6 py-2 text-gray-400 hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddTerm}
                  disabled={!newTerm.trim()}
                  className="flex items-center space-x-2 bg-gradient-to-r from-primary-500 to-secondary-500 px-6 py-2 rounded-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Check className="w-4 h-4" />
                  <span>Add Term</span>
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}

export default SearchTerms;
